import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogBody, DialogClose, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { PublicPlaylist, RoleGroupEnum } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCreatePlaylist } from "../api/create-playlist";
import { playlistsQueryKey } from "../api/featch-users-playlists";
import { ownedPlaylistsQueryKey } from "../api/fetch-owned-playlists";
import {
  AddPlaylistFormData,
  addPlaylistSchema,
} from "../types/add-playlist-schema";

type CreatePlaylistFormProps = {
  onClose: () => void;
  initialData?: AddPlaylistFormData;
};

export function CreatePlaylistForm(props: CreatePlaylistFormProps) {
  const { onClose } = props;
  const { organisation } = useOrganisation();
  const form = useForm<AddPlaylistFormData>({
    resolver: zodResolver(addPlaylistSchema),
    defaultValues: {
      name: "",
      isPublic: false,
    },
  });

  const { mutateAsync: createPlaylist, isSuccess } = useCreatePlaylist({
    onSuccess: (result) => {
      queryClient.setQueryData(
        ownedPlaylistsQueryKey(organisation.id),
        (oldData: PublicPlaylist[] | undefined) => {
          if (!oldData) return [result];
          return [result, ...oldData];
        },
      );
      queryClient.setQueryData(
        playlistsQueryKey(organisation.id),
        (oldData: PublicPlaylist[] | undefined) => {
          if (!oldData) return [result];
          return [result, ...oldData];
        },
      );
    },
  });

  async function handleCreatePlaylist(data: AddPlaylistFormData) {
    try {
      await createPlaylist({ orgId: organisation.id, data });
      onClose();
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCreatePlaylist)}>
        <DialogBody>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isRequired>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {organisation.roleGroup !== RoleGroupEnum.VIEWER && (
              <FormField
                control={form.control}
                name="isPublic"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="ml-2 mt-0">
                        <FormLabel className="text-base">
                          Publish to Organisation?
                        </FormLabel>
                        <FormDescription>
                          Allow this playlist to be visible to other users
                          within this organisation?
                        </FormDescription>
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </DialogClose>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting || isSuccess}
            isAnimated
          >
            Create
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
